import {
  Alert,
  Box,
  Container,
  DatePicker,
  ExpandableSection,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import {
  Content,
  ContentAudience,
  ContentNode,
  ContentWorkflow,
  ContentWorkingCopy,
  WorkflowEventTypes,
  WorkingCopyKey,
} from '@amzn/awscat-pattern-library-service-model';
import React, { useContext, useMemo, useState } from 'react';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import {
  contentAudienceBadgeDisplayNames,
  contentSubTypeDisplayNames,
} from '../../utils/DisplayNames';
import { PostAudienceOptions, PostTypeOptions } from '../../utils/SelectableOptions';
import BrowserHistory from '../../utils/BrowserHistory';
import * as UtilApi from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import PostControls from './PostControls';
import { ContentWorkingCopyManager } from '../../../common/ContentWorkingCopyManager';
import truncateHtml from 'truncate-html';
import { createWorkflowEventWithStatePolling } from '../../../api/CreateWorkflowEventWithStatePolling';
import { ContentSectionKey } from '../../../model/ContentSchemaSection';
import EditorInput from '../../../editor/EditorInput';
import { getHTML, TiptapEditorView, TiptapOutputType } from '@amzn/awscat-editor';
import EditorWrapper from '../../../editor/EditorWrapper';
import { WorkingCopyStatusType } from '../../common/WorkingCopyStatus';
import ErrorBoundary from '../../../editor/ErrorBoundary';
import { AppContext } from '../../../contexts/AppContext';
import { validateAndUpdateLinksInContentData } from '../../utils/ContentLinksUtils';

const MAX_PREVIEW_LENGTH_BY_CHARS = 700;
const MAX_PREVIEW_LENGTH_BY_CHARS_WITH_IMAGES = 500;

interface Props {
  post: Content;
  postId?: string;
  audience: ContentAudience;
  workflow?: ContentWorkflow;
  header: string;
  onPostSaved?: () => void;
  fetchImage: (srcUrl: string) => Promise<string>;
}

const PostEditingViewer = (props: Props) => {
  const workingCopyKey = WorkingCopyKey.contentworkingcopy;
  let savingLocalWorkingCopy: any = null;

  const { post, audience, workflow, onPostSaved, fetchImage } = props;
  const appContext = useContext(AppContext);
  const { entities } = appContext;
  const mplService = useMemo(() => MplServiceClientFactory.create(), []);
  const workingCopyMngr = new ContentWorkingCopyManager();

  const [updatedPost, setUpdatedPost] = useState(post);
  const [updatedAudience, setUpdatedAudience] = useState(audience);
  const [workingCopyStatus, setWorkingCopyStatus] = useState(WorkingCopyStatusType.Pending);
  const [isPreview, setIsPreview] = useState(false);
  const { contentMetadata, contentTypeMetadata, templateMetadata } = updatedPost;

  const [validationError, setValidationError] = useState({
    contentName: contentMetadata.contentName ? null : constants.REQUIRED,
    contentSubType: contentTypeMetadata.contentSubType ? null : constants.REQUIRED,
    publishDate: templateMetadata.publishDate ? null : constants.REQUIRED,
    audience: audience ? null : constants.REQUIRED,
  });
  const [showValidationError, setShowValidationError] = useState(false);
  const [contentDataLinkValidationError, setContentDataLinkValidationError] = useState<
    Array<{ key: string; value: string }>
  >([]);

  const updateMetadata = (key: string, val: string) => {
    // @ts-expect-error TS7053 - strict migration
    updatedPost.contentMetadata[key] = val;
    updatePostContent(updatedPost);
    updateValidationError(key, val);
  };

  const updateContentTypeMetadata = (key: string, val: string) => {
    // @ts-expect-error TS7053 - strict migration
    updatedPost.contentTypeMetadata[key] = val;
    updatePostContent(updatedPost);
    updateValidationError(key, val);
  };

  const updateTemplateMetadata = (key: string, val: string) => {
    updatedPost.templateMetadata[key] = val;
    updatePostContent(updatedPost);
    updateValidationError(key, val);
  };

  const updateContentSection = (
    sectionId: string,
    contentSectionKey: ContentSectionKey,
    val: TiptapOutputType,
  ) => {
    const updatedSection = updatedPost.contentData?.contentSections.find((s) => s.id === sectionId);
    if (updatedSection) {
      updatedSection[contentSectionKey] = val;
      setUpdatedPost(updatedPost);
    }
  };

  const saveLocalWorkingCopy = () => {
    if (!savingLocalWorkingCopy && workflow) {
      setWorkingCopyStatus(WorkingCopyStatusType.Saving);
      savingLocalWorkingCopy = setTimeout(async () => {
        try {
          const { contentId: postId } = workflow;

          await workingCopyMngr.createContentWorkingCopy(
            postId,
            workingCopyKey,
            ContentWorkingCopy.create(updatedPost, updatedAudience),
          );
          setWorkingCopyStatus(WorkingCopyStatusType.Saved);
        } catch (e) {
          setWorkingCopyStatus(WorkingCopyStatusType.Error);
        } finally {
          savingLocalWorkingCopy = null;
        }
      }, 3000);
    }
  };

  const updatePostContent = (postContent: Content) => {
    setUpdatedPost({ ...postContent });
    saveLocalWorkingCopy();
  };

  const { contentSubType } = updatedPost.contentTypeMetadata;

  const detailContainer = (
    <ExpandableSection
      variant="container"
      defaultExpanded
      header={<Header variant="h3">Details</Header>}
    >
      <SpaceBetween direction="vertical" size="m">
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 3 }, { colspan: 2 }]}>
          <FormField
            label="Title"
            errorText={showValidationError && validationError.contentName}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Input
              value={updatedPost.contentMetadata.contentName}
              onChange={(event) => updateMetadata('contentName', event.detail.value)}
              placeholder="Post title"
              ariaRequired
            />
          </FormField>
          <FormField
            label="Post type"
            errorText={showValidationError && validationError.contentSubType}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              selectedOption={{
                label: contentSubTypeDisplayNames.get(contentSubType),
                value: contentSubType,
              }}
              onChange={({ detail }) =>
                // @ts-expect-error TS2345 - strict migration
                updateContentTypeMetadata('contentSubType', detail.selectedOption.value)
              }
              options={PostTypeOptions}
              selectedAriaLabel="Choose an option"
              ariaRequired
            />
          </FormField>
        </Grid>
        <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }]}>
          <FormField
            label="Publish date"
            errorText={showValidationError && validationError.publishDate}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <DatePicker
              onChange={({ detail }) => updateTemplateMetadata('publishDate', detail.value)}
              value={updatedPost.templateMetadata?.publishDate}
              openCalendarAriaLabel={(selectedDate) =>
                'Choose Date' + (selectedDate ? `, selected date is ${selectedDate}` : '')
              }
              nextMonthAriaLabel="Next month"
              placeholder="YYYY/MM/DD"
              previousMonthAriaLabel="Previous month"
              todayAriaLabel="Today"
              ariaRequired
            />
          </FormField>
          <FormField
            label="Audience"
            errorText={showValidationError && validationError.audience}
            i18nStrings={{ errorIconAriaLabel: 'Error' }}
          >
            <Select
              selectedOption={{
                label: contentAudienceBadgeDisplayNames.get(updatedAudience),
                value: updatedAudience,
              }}
              options={PostAudienceOptions}
              // @ts-expect-error TS2345 - strict migration
              onChange={({ detail }) => updateAudience(detail.selectedOption.value)}
              selectedAriaLabel="Choose audience"
              ariaRequired
            />
          </FormField>
          {workflow && <FormField label="Version">V{workflow.draftWorkflowId}</FormField>}
        </Grid>
      </SpaceBetween>
    </ExpandableSection>
  );

  const updateAudience = (val: string) => {
    setUpdatedAudience(val as ContentAudience);
    updateValidationError('audience', val);
  };

  const renderPostContentSectionWithEntities = (section: ContentNode) => {
    const postContentSection = renderPostContentSection(section);
    return postContentSection;
  };

  const renderPostContentSection = (section: ContentNode) => {
    if (workflow) {
      return (
        <EditorInput
          contentId={workflow?.contentId}
          sectionId={section.id}
          data={section.contentData}
          readonly={false}
          onContentChanged={updateContentSection}
        />
      );
    } else {
      return (
        <EditorWrapper
          model={section.contentData}
          readOnly={false}
          // @ts-expect-error TS7006 - strict migration
          onModelChange={(val) =>
            updateContentSection(section.id, ContentSectionKey.ContentData, val)
          }
        />
      );
    }
  };

  const sectionContainers = updatedPost.contentData.contentSections.map((s) => {
    return (
      <ExpandableSection
        key={s.id}
        variant="container"
        defaultExpanded
        header={<Header variant="h3">{s.title}</Header>}
      >
        <SpaceBetween size="m">{renderPostContentSectionWithEntities(s)}</SpaceBetween>
      </ExpandableSection>
    );
  });

  const updateValidationError = (key: string, val: string) => {
    // @ts-expect-error TS7053 - strict migration
    validationError[key] = val ? null : constants.REQUIRED;
    setValidationError(validationError);
  };

  const validateForm = () => {
    // Form is valid if all values under validationError is null.
    setShowValidationError(true);

    // Validate links in content
    const linkErrors = validateAndUpdateLinksInContentData(updatedPost);
    setContentDataLinkValidationError(linkErrors);

    if (Object.values(validationError).some((k) => k) || linkErrors.length > 0) {
      return false;
    }
    return true;
  };

  const savePost = async () => {
    if (!validateForm()) {
      return;
    }
    getTruncateHtml(getHTML(updatedPost.contentData?.contentSections[0]?.contentData));

    if (!workflow) {
      const res = await createPost(updatedPost, updatedAudience);
      BrowserHistory.push(`/post-viewer/${res.contentId}`);
    } else {
      await updatePost(updatedPost, updatedAudience);
      // @ts-expect-error TS2722 - strict migration
      await onPostSaved();
    }
  };

  const getTruncateHtml = (html: string) => {
    if (html) {
      let truncated = truncateHtml(html, MAX_PREVIEW_LENGTH_BY_CHARS);
      if (truncated.includes('<img')) {
        truncated = truncateHtml(truncated, MAX_PREVIEW_LENGTH_BY_CHARS_WITH_IMAGES);
      }

      updateTemplateMetadata('summaryPreview', truncated);
    }
  };

  const createPost = async (postContent: Content, contentAudience: ContentAudience) => {
    const res = await mplService.createContent(postContent, contentAudience);
    let attempts = 0;
    while (attempts < 3) {
      await UtilApi.promiseTimeout(1000);
      try {
        await mplService.getContent(res.contentId);
        break;
      } catch (e) {
        attempts++;
      }
    }
    return res;
  };

  const updatePost = async (postContent: Content, contentAudience: ContentAudience) => {
    const postId = workflow?.contentId;

    if (!postId) {
      return;
    }

    if (contentAudience !== props.audience) {
      await mplService.updateWorkflowContentAudience(postId, contentAudience);
    }

    const res = await mplService.updateContent(postId, postContent, true);
    await workingCopyMngr.deleteWorkingCopies(postId, workingCopyKey);
    return res;
  };

  const deletePost = async (postId: string) => {
    BrowserHistory.push('/whats-new');
  };

  const publishPost = async (postId: string) => {
    // @ts-expect-error TS2339 - strict migration
    const { draftWorkflowId } = workflow;
    await savePost();
    await createWorkflowEventWithStatePolling(
      postId,
      draftWorkflowId,
      WorkflowEventTypes.PUBLISH,
      '',
      {},
    );
    BrowserHistory.push('/whats-new');
  };

  const previewDisplay = (
    <Container>
      <Box display="inline-block" color="text-body-secondary">
        <Box variant="strong">Posted</Box> {updatedPost.templateMetadata.publishDate}{' '}
        <span>&middot;</span>{' '}
        <Box variant="strong">
          {contentSubTypeDisplayNames.get(updatedPost?.contentTypeMetadata?.contentSubType)}
        </Box>
      </Box>
      <Box variant="h2">{updatedPost.contentMetadata.contentName}</Box>
      <ErrorBoundary
        fallback={
          <TiptapEditorView
            model={updatedPost?.contentData?.contentSections[0]?.contentData}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={fetchImage}
          />
        }
      >
        <TiptapEditorView
          model={updatedPost?.contentData?.contentSections[0]?.contentData}
          // @ts-expect-error TS2322 - strict migration
          entities={entities}
          fetchImage={fetchImage}
        />
      </ErrorBoundary>
    </Container>
  );
  const editingDisplay = (
    <Form header={<Header variant="h2">{workflow ? 'Edit Post' : 'Create Post'}</Header>}>
      <SpaceBetween size="l">
        {showValidationError && contentDataLinkValidationError.length > 0 && (
          <Alert type="error" header="Link Validation Errors">
            <SpaceBetween size="xxs">
              {contentDataLinkValidationError.map((error) => (
                <Box key={error.key}>
                  <Box variant="strong" display="inline">
                    {error.key}:
                  </Box>{' '}
                  {error.value}
                </Box>
              ))}
            </SpaceBetween>
          </Alert>
        )}
        {detailContainer}
        {sectionContainers}
      </SpaceBetween>
    </Form>
  );

  return (
    <Grid className="create-post-form" gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
      {isPreview ? previewDisplay : editingDisplay}
      <div className={!isPreview ? 'create-post-control' : undefined}>
        <PostControls
          header={'Publish'}
          // @ts-expect-error TS2322 - strict migration
          workflow={workflow}
          onPreview={() => setIsPreview(true)}
          onExitPreview={() => setIsPreview(false)}
          onSave={savePost}
          onDelete={deletePost}
          onPublish={publishPost}
          autoSavingEnabled={!!savingLocalWorkingCopy}
        />
      </div>
    </Grid>
  );
};

export default PostEditingViewer;
